import ValidateModule from "./ValidateModule";

import type {ILayerView} from "@esWebAPI/es/model/Layer";
import type {SortOrder} from "@esWebAPI/es/model/Feature";
import type {Criteria} from "@esWebAPI/es/model/Criteria";
import type {GeoMap} from "@esWebAPI/es/model/GeoMap";
import type AjaxRequestBroker from "../AjaxRequestBroker";
import type { IEventChannel, ApiOpts } from "../model/Models";
import type CacheModule from "./CacheModule";

/** @class es.module.[resource]
 * Object providing [resource].
 */
export default  class LayerViewModule {
        eventChannel: IEventChannel;
        readonly rqBroker: AjaxRequestBroker;
        readonly resourceName: string;
        readonly cache: CacheModule;

        constructor (eventChannel: any, rqBroker: AjaxRequestBroker, resourceName: string, cache: CacheModule) {
            this.eventChannel = eventChannel;
            this.rqBroker = rqBroker;
            this.resourceName = resourceName;
            this.cache = cache;

            this.eventChannel
                .addListener("onDestroy", () => {
                    this.eventChannel = null; // prevent memory leaks when the map widget is destroyed
                });
        }
        
        /**
         * @function es.module.[resource].get
         * Get the resource from the server
         * @return a jquery deferred object resolved when the request has completed
         */
        get(id: number, opts?: ApiOpts): JQuery.Deferred<ILayerView>{
            if (!ValidateModule.checkId(id)) {
                return es.util.deferred.rejected();
            }
            
            const url = es.api.getURL(this.resourceName) + "/" + id;
            return this.cache.doGet(this.rqBroker, this.resourceName, url, id, opts);
        }
        
        /**
         * @function es.module.[resource].get
         * Get the resource from the server
         * @param ids
         * @param opts
         * @return a jquery deferred object resolved when the request has completed
         */
        getByIds(ids: number[], opts?: ApiOpts): JQuery.Deferred<ILayerView[]>{
            if (!ValidateModule.checkIds(ids)) {
                return es.util.deferred.rejected();
            }
            
            const url = es.api.getURL(this.resourceName) + "/ids?ids=" + es.util.url.stringify(ids);
            return this.cache.doGet(this.rqBroker, this.resourceName, url, ids, opts);
        }
        
        /**
         * @function es.module.[resource].get
         * Get the resource from the server
         * @param criteria
         * @param sortOrder
         * @param offset
         * @param length
         * @param opts
         * @return a jquery deferred object resolved when the request has completed
         */
        list(criteria?: Criteria, sortOrder?: SortOrder, offset?: number, length?: number, opts?: ApiOpts): JQuery.Deferred<ILayerView[]>{
            return this.cache.doGetList(this.rqBroker, this.resourceName, criteria, sortOrder, offset, length, opts);
        }

        /**
         * @function es.module.[resource].get
         * Get the resource from the server
         * @param geoMapId
         * @param opts
         * @return a jquery deferred object resolved when the request has completed
         */
        getByGeoMap(geoMapId: number, opts?: ApiOpts) : JQuery.Deferred<ILayerView[]> {
            if (!ValidateModule.checkId(geoMapId)) {
                return es.util.deferred.rejected();
            }

            const url = es.api.getURL(this.resourceName) + "/get_by_geomap/" + geoMapId;
            return this.cache.doGet(this.rqBroker, "LayerView_GeoMap", url, geoMapId, opts);
        }

        getByGeoMapAndType(geoMapId: number, type: ILayerView["$type"], opts?: ApiOpts) : JQuery.Deferred<ILayerView[]> {
            if (!ValidateModule.checkId(geoMapId)) {
                return es.util.deferred.rejected();
            }
            const url = es.api.getURL(this.resourceName) + "/get_by_geomap/" + geoMapId + "/" + type;
            return this.rqBroker.doGet({url: url, opts: opts});
        }
        
        /**
         * @function es.module.[resource].count
         * Get a count of resources matching the given Criteria
         * @param criteria
         * @param opts
         * @return long
         */
        count (criteria?: Criteria, opts?: ApiOpts): JQueryDeferred<number> {
            const url = es.api.getURL(this.resourceName) + "/count" + (criteria ? "?criteria=" + es.util.url.stringify(criteria) : "");
            return this.rqBroker.doGet({url: url, opts: opts});
        }

        /**
         * @function es.module.[resource].post
         * POST a new resource to the server (note: id attrib must be -1)
         * @return a jquery deferred object resolved when the request has completed
         * @param resourceObj
         * @param opts
         */
        create (resourceObj: ILayerView, opts?: ApiOpts): JQuery.Deferred<ILayerView>{
            if (!ValidateModule.checkMandatoryArg(resourceObj)) {
                return es.util.deferred.rejected();
            }
            
            //es.log.info("layerview create", resourceObj);

            // we need access to the header so we can see what the geomaps last modified date is
            opts = $.extend(undefined, {resolveWithXHR: true});
            
            return this.rqBroker.doPost({
                url: es.api.getURL(this.resourceName),
                opts: opts,
                data: resourceObj
            }).then((layerView: ILayerView, jqXHR: JQuery.jqXHR) => {
                return this.onLayerCreationHandler(layerView, jqXHR);
            });
        }


        private onLayerCreationHandler(layerView: ILayerView, _jqXHR: JQuery.jqXHR){
            this.cache.setEntity(this.resourceName, layerView);
            //var geoMapLastModified = parseInt(jqXHR.getResponseHeader("es_geomap_last_modified"));

            return es.api.layerInfo.getForLayerView(layerView.id).then(layerInfo => {
                this.cache.clear("GeoMap");
                this.cache.clear("LayerView_GeoMap");
                this.cache.clear("LayerView");
                this.cache.clear("Security", "ownership_layerview");

                this.cache.clear("LayerMeta_GeoMap");

                return es.api.appResources.setWorkingCopyChanged(layerView.geoMapId).then(async (geoMap: GeoMap) => {
                    if (this.eventChannel) {
                        await this.eventChannel.fireEvent("onCreateLayerView", layerView, layerInfo, geoMap.lastModified);
                    }

                    return es.util.deferred.resolved(layerView, layerInfo);
                });
            });
        }
        
        /**
         * @function es.module.[resource].put
         * PUT update an existing resource on the server
         * @return a jquery deferred object resolved when the request has completed
         * @param resourceObj
         * @param opts
         * @param isSuppressMetaChange
         */
        update (resourceObj: ILayerView, opts?: ApiOpts, isSuppressMetaChange?: boolean): JQueryPromise<ILayerView>{
            return this._update(resourceObj, opts).then((rs) => {
                return this.fireOnUpdateLayerView(rs, isSuppressMetaChange);
            });
        }

        private _update (resourceObj: ILayerView, opts?: ApiOpts): JQueryPromise<ILayerView> {
            if (!ValidateModule.checkMandatoryArg(resourceObj)) {
                return es.util.deferred.rejected();
            }

            if (es.util.object.equals(resourceObj.criteria, {'$type': 'Not', 'criteria': {'$type': 'Equal', attr: 'ES_GEOM_STATUS_CODE', value: 'TO_BE_SET'}})) {
                es.error.handleJSError("Error Saving Map. To report this issue, please use the in-app chat or contact help@espatial.com to report error: TOR9-10083.");
                return $.Deferred().reject();
            }

            // we need access to the header so we can see what the geomaps last modified date is
            opts = $.extend(undefined, {resolveWithXHR: true});
            return this.rqBroker.doPut({
                url: es.api.getURL(this.resourceName) + "/" + resourceObj.id,
                opts: opts,
                data: resourceObj
            }).then((rs: ILayerView, _jqXHR: JQuery.jqXHR) => {
                //cache.clearAll();
                this.cache.clear("FeatureSource_Criteria_LayerView", rs.id);
                this.cache.clear("GeoMap", rs.geoMapId);
                this.cache.clear("LayerInfo_LayerView", rs.id);
                this.cache.clear("LayerView_GeoMap", rs.geoMapId);
                this.cache.clear("LayerMeta_GeoMap", rs.geoMapId);
                this.cache.clear("Thematic");

                this.cache.setEntity(this.resourceName, rs);
                return rs;
            });
        }

    private fireOnUpdateLayerView(rs: ILayerView, isSuppressMetaChange?: boolean): JQueryPromise<ILayerView> {
        // If we catch this here and then fire onUpdateLayerView event after fetching layerInfo.getForLayerView then we might
        // overwrite a newer lastModifiedDate with an older one (TOR9-6191)
        //var geoMapLastModified = parseInt(jqXHR.getResponseHeader("es_geomap_last_modified"));
        return $.when(
            es.api.layerInfo.getForLayerView(rs.id),
            es.api.geoMap.get(rs.geoMapId, {"noCache": true})
        ).then((layerInfo, geoMap) => {
            this.eventChannel.fireEvent("onUpdateLayerView", rs, layerInfo, geoMap.lastModified, isSuppressMetaChange);
            return rs;
        });
    }


    /**
     * @function es.module.[resource].del
         * Get the resource from the server
         * @return a jquery deferred object resolved when the request has completed
         */
        remove (id: number, opts?: ApiOpts): JQueryPromise<void> {
            if (!ValidateModule.checkId(id)) {
                return es.util.deferred.rejected();
            }
            
            opts = $.extend(opts, {resolveWithXHR: true});
            return this.rqBroker.doDelete(
                {url: es.api.getURL(this.resourceName) + "/" + id, opts: opts}
            ).then((_rs: boolean, jqXHR: JQuery.jqXHR) => {
                this.cache.clear(this.resourceName, id);
                this.cache.clear("LayerInfo_LayerView");
                this.cache.clear("LayerView_LayerInfo");
                this.cache.clear("LayerView_LayerMeta");
                this.cache.clear("LayerView_GeoMap");
                this.cache.clear("LayerView");
                this.cache.clear("Security", "ownership_layerview");
                this.cache.clear("GeoMap");
                // If we catch this here and then fire onUpdateLayerView event after fetching layerInfo.getForLayerView then we might 
                // overwrite a newer lastModifiedDate with an older one (TOR9-6191)
                const geoMapId = parseInt(jqXHR.getResponseHeader("es_geomap_id") as string);
                return es.api.appResources.setWorkingCopyChanged(geoMapId).then((geoMap: GeoMap) => {
                    this.eventChannel?.fireEvent("onRemoveLayerView", id, geoMapId, geoMap.lastModified);
                    return es.util.deferred.resolved();
                });
            });
        }
        
        /**
         * @function es.module.LayerView.hasCriteriaFeatureSource
         * Checks if the layer view feature source has some criteria applied (if so, it will imply we can't add a NN criteria)
         */
        hasCriteriaFeatureSource(id: number, opts?: ApiOpts): JQuery.Deferred<boolean> {
            if (!ValidateModule.checkId(id)) {
                return es.util.deferred.rejected();
            }
            
            // @ts-ignore
            return this.getFeatureSourceCriterias(id, opts).then(function(rs) {
                return (!!(rs.removableCriteria || rs.unremovableCriteria));
            });
        }
        
        /**
         * @function es.module.LayerView.getFeatureSourceCriterias
         * Gets the layer view feature source criterias (removableCriteria and unremovableCriteria)
         */
        getFeatureSourceCriterias(id: number, opts?: ApiOpts) : JQuery.Deferred<{removableCriteria?: Criteria, unremovableCriteria?: Criteria}> {
            if (!ValidateModule.checkId(id)) {
                return es.util.deferred.rejected();
            }
            const url = es.api.getURL('featuresource_criteria') + '/layerview/' + id;
            //return rqBroker.doGet({'url': url, 'opts': opts});
            return this.cache.doGet(this.rqBroker, "FeatureSource_Criteria_LayerView", url, id, opts);
        }
        
        /**
         * @function es.module.LayerView.updateFeatureSourceRemovableCriteria
         * Update the layer view feature source removableCriteria
         */
        updateRemovableCriteria (id: number, removableCriteria: Criteria, opts?: ApiOpts): JQueryPromise<{created: number, id: number, lastModified:number, removableCriteria: Criteria}> {
            return this._updateRemovableCriteria(id, removableCriteria, opts).then(this.fireOnUpdateLayerViewRemovableCriteria.bind(this));
        }

        private _updateRemovableCriteria (id: number, removableCriteria: Criteria, opts?: ApiOpts) {
            if (!ValidateModule.checkId(id)) {
                return es.util.deferred.rejected();
            }
            
            const url = es.api.getURL('featuresource_criteria') + '/layerview/' + id + '/removableCriteria';
            return this.rqBroker.doPut({'url': url, 'data': removableCriteria, 'opts': opts}).done(
                (rs: {created: number, id:number, lastModified: number, removableCriteria: Criteria}) => {
                    this.cache.clear("LayerInfo_LayerView");
                    this.cache.clear("LayerView_LayerInfo");
                    this.cache.clear("LayerView_GeoMap");
                    this.cache.clear("LayerView");
                    this.cache.clear("GeoMap");
                    this.cache.clear("LayerInfo");
                    this.cache.clear("Thematic_LIST_COLOR", id);
                    this.cache.clear("Thematic_LIST_SIZE", id);
                    this.cache.clear("Thematic_LIST_SYMBOL", id);
                    return rs;
                });
        }

        private fireOnUpdateLayerViewRemovableCriteria(rs: {created: number, id:number, lastModified: number, removableCriteria: Criteria}, layerViewId: number) {
            this.eventChannel.fireEvent("onUpdateLayerViewRemovableCriteria", rs, layerViewId);
            return rs;
        }

        public updateWithRemovableCriteria(layerView: ILayerView, removableCriteria: Criteria, opts?: ApiOpts) {
            const isSpatialQueryInLayerView = es.util.criteria.contains(layerView.criteria, {"$type": "BoundsCriteria"})
                || es.util.criteria.contains(layerView.criteria, {"$type": "Interacts"})
                || es.util.criteria.contains(layerView.criteria, {"$type": "Nearest"})
                || es.util.criteria.contains(layerView.criteria, {"$type": "WithinDistance"});

            let dfd;
            if (isSpatialQueryInLayerView) {
                // TOR9-18084 if spatial query is in layerview, could be removing from removable criteria
                // we can't have (even if small time) where it exists in both (causing error when it's a spatial operation)
                dfd = this._updateRemovableCriteria(layerView.id, removableCriteria, opts).then((rcrs) => {
                    return this._update(layerView).then(function(lvrs) {
                        return es.util.deferred.resolved(lvrs, rcrs);
                    });
                });
            } else {
                dfd = this._update(layerView).then((lvrs) => {
                    return this._updateRemovableCriteria(layerView.id, removableCriteria, opts).then(function(rcrs) {
                        return es.util.deferred.resolved(lvrs, rcrs);
                    });
                });
            }

            return dfd.then((lvrs, rcrs) => {
                // TOR9-19365 if spatial query is in layerview, could be removing from removable criteria
                // we can't have (even if small time) where it exists in both (causing error when it's a spatial operation)
                let eventsDfd;
                if (isSpatialQueryInLayerView) {
                    this.fireOnUpdateLayerViewRemovableCriteria(rcrs, layerView.id); // this just fires it, no deferred used
                    eventsDfd = this.fireOnUpdateLayerView(lvrs); // this returns deferred
                } else {
                    eventsDfd = this.fireOnUpdateLayerView(lvrs).then(() => this.fireOnUpdateLayerViewRemovableCriteria(rcrs, layerView.id)); // wait for lv to be updated and all events processed before firing removable criteria event
                }
                
                return eventsDfd.then(() => es.util.deferred.resolved(lvrs, rcrs));
            });
        }
        
        /**
         * @function es.module.LayerView.getTotalByAttribute
         * Get total of specified attributes
         * @param id
         * @param attrNames
         * @param criteria optional. note: this criteria would be in addition to any criteria already on the layerview and feature source.
         * @param opts
         */
        getTotalByAttribute (id: number, attrNames: string[], criteria?: Criteria, opts?: ApiOpts) {
            if (!ValidateModule.checkId(id) ||
                !ValidateModule.checkMandatoryArg(attrNames)) {
                return es.util.deferred.rejected();
            }
            
            let url = es.api.getURL(this.resourceName) + '/get_total_by_attribute/' + id;
            url = url + "?attrNames=" + es.util.url.stringify(attrNames);
            if(criteria){
                url = url + "&criteria=" + es.util.url.stringify(criteria);
            }
            return this.rqBroker.doGet({'url': url, 'opts': opts});
        }
        
        /**
         * @function es.module.LayerView.addLinkedAttribute
         * Add specified linked attribute to layerview where geom is contained within geom of sourceLayerMetaId
         * @param id layerview to copy attribute to
         * @param sourceLayerMetaId layermeta to copy attribute from
         * @param sourceAttrName attribute to copy
         * @param opts
         */
        addLinkedAttribute(id: number, sourceLayerMetaId: number, sourceAttrName:string, opts?: ApiOpts) {
            if (!ValidateModule.checkId(id) ||
                !ValidateModule.checkId(sourceLayerMetaId) ||
                !ValidateModule.checkMandatoryArg(sourceAttrName)) {
                return es.util.deferred.rejected();
            }
            
            let url = es.api.getURL(this.resourceName) + '/add_linked_attribute/' + id;
            url = url + "?sourceLayerMetaId=" + sourceLayerMetaId;
            url = url + "&sourceAttrName=" + sourceAttrName;
    
            return this.rqBroker.doPost({'url': url, 'opts': opts});
        }
        
        /**
         * Create a layerView on specified geomap using specified layerMetaId. If layerMetaId is not currently available to user then it will be imported from DataStore if possible.
         */
        createLayerViewFromLayerMetaId(layerMetaId: number, geoMapId: number, opts?: ApiOpts): JQueryPromise<ILayerView> {
            if (!ValidateModule.checkId(layerMetaId) ||
                !ValidateModule.checkId(geoMapId)) {
                return es.util.deferred.rejected();
            }
            
            const url = es.api.getURL(this.resourceName) + '/layerMetaId/' + layerMetaId + "?geoMapId=" + geoMapId;
            return this.rqBroker.doPost({
                'url': url,
                'opts': opts
            }).then((layerView: ILayerView, jqXHR: JQuery.jqXHR) => {
                return this.onLayerCreationHandler(layerView, jqXHR);
            });
        }
        
        /**
         * Create a layerview on specified geoMap using sample file
         */
        createLayerViewFromSample(geoMapId: number, opts?: {}): JQuery.Deferred<ILayerView> {
             if (!ValidateModule.checkId(geoMapId)) {
                return es.util.deferred.rejected();
            }
            
            const url = es.api.getURL(this.resourceName) + '/sample?geoMapId=' + geoMapId;
            return this.rqBroker.doPost({
                'url': url,
                'opts': opts
            }).then((layerView: ILayerView, jqXHR: JQuery.jqXHR) => {
                return this.onLayerCreationHandler(layerView, jqXHR);
            });
        }

        /**
         * Create the application (and API) default MapScopeCustomPointLayerView (aka 'address point layerview')
         */
        createDefaultMapScopeCustomPointLayerView (geoMapId: number, opts?: ApiOpts): JQueryPromise<ILayerView> {
            if (!ValidateModule.checkId(geoMapId)) {
                return es.util.deferred.rejected();
            }

            const url = es.api.getURL(this.resourceName) + '/map_scope_custom_point_layer_view/' + geoMapId;
            return this.rqBroker.doPost({'url': url, 'opts': opts}).then((layerView: ILayerView, jqXHR: JQuery.jqXHR) => {
                return this.onLayerCreationHandler(layerView, jqXHR);
            });
        }
        
        /**
         * Given an attribute name (e.g. NAME1) return the linked attribute name that will be generated for it (e.g. ES_LINK_123_NAME1).
         * This method is public as it is useful to be able to query if a layerview already has a certain linked attribute
         * and, if so, what that attributes name is.
         * @param layerViewId
         * @param attrName
         * @param opts
         */
        getLinkedAttributeName (layerViewId: number, attrName: string, opts?: ApiOpts): JQuery.Deferred<string> {
            if (!ValidateModule.checkId(layerViewId) ||
                !ValidateModule.checkMandatoryArg(attrName)) {
                return es.util.deferred.rejected();
            }
            
            const url = es.api.getURL(this.resourceName) + '/get_linked_attribute_name/' + layerViewId + "?attrName=" + attrName;
            return this.rqBroker.doGet({'url': url, 'opts': opts});
        }

        /**
         * Given a LayerView it returns a Map that contains which Attribute Names
         *  should be used for GeocodePrecision levels (STREET, ZIP, CITY, STATE, COUNTRY)
         * @param id
         * @returns {*}
         */
        getAddressMeta (id: number){
            const url = es.api.getURL(this.resourceName) + "/address_meta/" + id;
            return this.cache.doGet(this.rqBroker, "LayerView_Address_Meta", url, id);
        }
        
        getByGeoMapExcludeAnalysis(id: number, opts?: ApiOpts): JQueryPromise<ILayerView[]> {
            return $.when(
                es.api.layerView.getByGeoMap(id, opts),
                es.api.analysis.getByGeoMapId(id, opts)
            ).then(function(layerViews, analysis){

                let analysisGeneratedIds: any[] = [];
                if (analysis){
                    analysisGeneratedIds = es.api.analysis.getGeneratedLayerViewIds(analysis);
                }

                const rs: ILayerView[] = [];
                _.each(layerViews, function(layerView: ILayerView) {
                    if (!_.includes(analysisGeneratedIds, layerView.id)){
                        rs.push(layerView);
                    }
                });
                return rs;
            });
        }

        createVoronoi(id: number, opts?: ApiOpts) {
            const url = es.api.getURL(this.resourceName) + "/voronoi/" + id;
            return this.rqBroker.doPost({'url': url, 'opts': opts});
        }

        getVoronoiGeoms(id: number, opts?: ApiOpts) {
            const url = es.api.getURL(this.resourceName) + "/voronoi/" + id;
            return this.rqBroker.doGet({'url': url, 'opts': opts});
        }

        getAttributesSpecialFunction(layerViewId: number, opts?: ApiOpts) {
            return this.get(layerViewId, opts).then(lv => {

                if(!lv.layerMetaId) {
                    return es.util.deferred.resolved([]);
                }

                return es.api.layerMeta.get(lv.layerMetaId, opts).then(lm => {
                    return lm?.attributesSpecialFunction;
                });
            });
        }

        pbfPreload(layerView: ILayerView, criteria?: Criteria, opts?: ApiOpts) {
            const url = es.api.getURL('pbf') + '/preload';
            const data = {layerView, criteria};
            return this.rqBroker.doPost({'url': url, 'data': data, 'opts': opts});
        }

        pbfGetClusterChildren(layerViewId: number, clusterId: number, opts?: ApiOpts) {
            const url = es.api.getURL('pbf') + '/children?layerViewId=' + layerViewId + "&clusterId=" + clusterId;
            return this.rqBroker.doGet({'url': url, 'opts': opts});
        }
    }

